/**
 * Map partner-db's CleverTap-style calls onto the Wisetap ingest schema
 * (wisetap.oa_user_events / oa_user_profiles).
 *
 * `properties` is a String column in ClickHouse and the reference payloads send
 * scalars as strings, so scalar prop values are coerced to string and nested
 * objects are JSON-stringified for parity.
 */
import type {
  ContextSnapshot,
  EventProps,
  OaUserEvent,
  OaUserProfile,
  Traits,
} from "./types";

const asStr = (v: unknown): string | null => (v == null ? null : String(v));

const stringifyProps = (props?: EventProps): Record<string, string> => {
  if (!props || typeof props !== "object") return {};
  return Object.keys(props).reduce<Record<string, string>>((acc, k) => {
    const v = props[k];
    if (v === null || v === undefined) return acc;
    acc[k] = typeof v === "object" ? JSON.stringify(v) : String(v);
    return acc;
  }, {});
};

/** One oa_user_events row. triggered_at stamped here (enqueue time). */
export const toEvent = (
  eventName: string,
  props: EventProps | undefined,
  ctx: ContextSnapshot,
  sessionId: string,
): OaUserEvent => {
  const src = props?.source ?? props?.screen_name;
  return {
    app_name: ctx.app_name,
    external_user_id: ctx.external_user_id,
    unique_device_id: ctx.unique_device_id,
    phone_number: ctx.phone_number,
    event_name: eventName,
    source: src != null ? String(src) : null,
    lat: ctx.lat != null ? String(ctx.lat) : null,
    lng: ctx.lng != null ? String(ctx.lng) : null,
    triggered_at: new Date().toISOString(),
    properties: stringifyProps(props),
    platform: ctx.platform,
    app_version: ctx.app_version,
    os_version: ctx.os_version,
    session_id: sessionId,
    device_model: ctx.device_model,
    is_test: String(ctx.is_test),
  };
};

/** Stamp ingested_at across a batch at flush time. */
export const stampIngestedAt = (rows: OaUserEvent[]): OaUserEvent[] => {
  const ingested_at = new Date().toISOString();
  return rows.map((r) => ({ ...r, ingested_at }));
};

const PROFILE_KNOWN_KEYS = new Set<string>([
  "name",
  "user_name",
  "email",
  "gender",
  "language",
  "city",
  "state",
  "pincode",
  "is_whatsapp_opted",
  "phone_number",
  "created_at",
  "createdAt",
  "updated_at",
]);

/**
 * One oa_user_profiles object. Every schema column is present on every call
 * (null when unknown). Unmapped scalar traits fall into `properties`.
 * created_at is taken from the user object when available; if absent we send
 * `now` (ReplacingMergeTree dedups by updated_at, so a real created_at from the
 * source is preferred to keep it stable).
 */
export const toProfile = (
  _userId: string | number,
  traits: Traits | undefined,
  ctx: ContextSnapshot,
): OaUserProfile => {
  const t = traits || {};
  const now = new Date().toISOString();
  const extra = Object.keys(t).reduce<Record<string, string>>((acc, k) => {
    const v = t[k];
    if (!PROFILE_KNOWN_KEYS.has(k) && v != null && typeof v !== "object") {
      acc[k] = String(v);
    }
    return acc;
  }, {});
  return {
    app_name: ctx.app_name,
    // Key on the same store-derived id as events (cp_db_id) so events and
    // profiles join — NOT the setUser() arg (data.id / team-leader id).
    external_user_id: ctx.external_user_id,
    phone_number: ctx.phone_number,
    name: asStr(t.cp_db_id ? t.db_name : t.name) || asStr(t.user_name),
    email: asStr(t.email),
    gender: asStr(t.gender),
    language: asStr(t.language),
    city: asStr(t.city),
    state: asStr(t.state),
    pincode: asStr(t.pincode),
    lat: ctx.lat != null ? String(ctx.lat) : null,
    lng: ctx.lng != null ? String(ctx.lng) : null,
    platform: ctx.platform,
    app_version: ctx.app_version,
    device_model: ctx.device_model,
    is_whatsapp_opted: String(!!t.is_whatsapp_opted),
    properties: extra,
    updated_at: asStr(t.updated_at) || now,
    created_at: asStr(t.created_at) || asStr(t.createdAt) || now,
  };
};
